import React, { useEffect, useState } from 'react';
import PageInstructions from '@shared/components/PageInstructions';
import { Button } from '@mui/material';
import GET_CHECK_POINT_BY_ID from '../../queries/getCheckPoint';
import ApolloError from '@shared/components/ApolloError';
import StickyHeader from '@shared/components/StickyHeader';
import CheckpointFilesTable from '../common/CheckpointFilesTable';
import PerformanceMetricsTable from '../common/PerformanceMetricsTable';
import { useQuery, useMutation, useReactiveVar } from '@apollo/client';
import { PAGE_VIEW_MODES } from '../../../../constants/page-view-modes';
import { Navigate, useNavigate } from 'react-router-dom';
import DELETE_CHECK_POINT from '../../queries/deleteCheckPoint';
import GET_CHECK_POINTS from '../../queries/getCheckPoints';
import RemoveCheckPointWarningModal from './RemoveCheckPointWarningModal';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import Nbsp from '@shared/components/Nbsp';
import LinkButton from '@shared/components/LinkButton';
import { getDataNotFoundPage } from '@shared/services/getDataNotFoundPageService';
import { userTier } from '@apollo-internal/cache';
import { buildURL } from '../../../../utils/stringExtensions';
import { checkPointData } from '../../checkPointsCache';
import CheckPointMapper from '../mappers/checkPointMapper';
import { useCheckPointFile } from '@shared/hooks/useCheckPointFile';

/**
 * @typedef {object} CheckPointContentProps
 * @property {number} orgUnitId
 * @property {number} checkPointId
 * @property {number} appraisalId
 * @property {string} [dataCollectionDate]
 */

/**
 * @param {CheckPointContentProps} props
 */
const CheckPointContent = props => {
  const { orgUnitId, checkPointId, appraisalId, dataCollectionDate } = props;
  const previousLink = `/check-points/${orgUnitId}`;

  const tier = useReactiveVar(userTier);
  const checkPointCache = useReactiveVar(checkPointData);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, error, loading } = useQuery(GET_CHECK_POINT_BY_ID, {
    variables: { checkPointId: checkPointId }
  });

  useEffect(() => {
    if (!data || !data.checkPoint) {
      return;
    }

    const checkPoint = CheckPointMapper.checkPointToCacheData(
      data.checkPoint,
      appraisalId,
      orgUnitId,
      checkPointId
    );

    checkPointData(checkPoint);
  }, [appraisalId, checkPointId, data, orgUnitId]);

  const { downloadFile } = useCheckPointFile();

  const [deleteCheckPoint, { loading: deletingCheckPoint, error: deletingCheckPointError }] =
    useMutation(DELETE_CHECK_POINT, {
      update: cache => cache.evict({ query: GET_CHECK_POINTS }),
      onCompleted: () => redirectToCheckPointsPage()
    });

  const handleFileDownload = async (event, record) => {
    event.preventDefault();

    await downloadFile(checkPointId, record.fileName);
  };

  const redirectToCheckPointsPage = () => {
    navigate(previousLink, {
      state: { checkPointModifiedSuccessfullyMessage: 'Check Point removed successfully.' }
    });
  };

  const handleOnConfirmButton = () => {
    setIsModalOpen(true);
  };

  const handleRemovalApproved = async () => {
    setIsModalOpen(false);
    await deleteCheckPoint({ variables: { checkPointId: checkPointId } });
  };

  const handleRemovalDeclined = () => {
    setIsModalOpen(false);
  };

  if (data?.checkPoint?.IsDraft) {
    return <Navigate to={previousLink} />;
  }

  if (data?.checkPoint === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (error || deletingCheckPointError) {
    return <ApolloError />;
  }

  const pageDescription =
    'Submitted check point files are displayed in the first table and can be downloaded by clicking the file name. ' +
    'Submitted performance report metrics are displayed in the second table, ' +
    'use the “View” text to see additional details. Click the “Edit” button to remove, change, ' +
    'and/or add files. Edit will also enable changes to editable metric fields. ' +
    'Clicking the “Remove” button will delete the check point and all affiliated data.';

  return (
    <>
      <StickyHeader>
        <div className='header-content'>
          <PageInstructions title='View Check Points' description={pageDescription} />
          <div className='action-group'>
            <div className='action-button-group'>
              <LinkButton
                data-testid='back-button'
                className='secondary-button flex view-check-point-back-link'
                type='button'
                to={previousLink}>
                Back
              </LinkButton>
              {data &&
                data.checkPointEditRemovalPermission.canUserEditOrRemoveSubmittedCheckPoint && (
                  <>
                    <LinkButton
                      data-testid='edit-button'
                      className='secondary-button flex edit-button'
                      type='button'
                      disabled={
                        deletingCheckPoint ||
                        !data.checkPointEditRemovalPermission
                          .isSubmittedCheckPointEditableOrRemovable
                      }
                      to={buildURL([
                        '/check-points',
                        orgUnitId,
                        'edit-check-point',
                        appraisalId,
                        checkPointId
                      ])}>
                      Edit
                    </LinkButton>
                    <Button
                      data-testid='remove-button'
                      className='primary-button lg'
                      type='submit'
                      disabled={
                        deletingCheckPoint ||
                        !data.checkPointEditRemovalPermission
                          .isSubmittedCheckPointEditableOrRemovable
                      }
                      onClick={handleOnConfirmButton}>
                      Remove Check Point
                    </Button>
                  </>
                )}
            </div>
          </div>
        </div>
      </StickyHeader>
      <div>
        <p className='reporter-name'>
          <strong>
            Reporter:
            <Nbsp />
          </strong>
          <SkeletonWrapper show={loading} renderComponent={() => data.checkPoint.ReporterName} />
        </p>
        <CheckpointFilesTable
          viewMode={PAGE_VIEW_MODES.VIEW}
          handleFileDownload={handleFileDownload}
          checkPointDocuments={checkPointCache.checkPointDocuments}
          loading={loading}
        />
        <PerformanceMetricsTable
          viewMode={PAGE_VIEW_MODES.VIEW}
          performanceMetrics={checkPointCache.performanceMetrics}
          checkPointId={checkPointId}
          appraisalId={appraisalId}
          orgUnitId={orgUnitId}
          loading={loading}
        />
      </div>
      <RemoveCheckPointWarningModal
        open={isModalOpen}
        appraisalId={appraisalId}
        dataCollectionDate={dataCollectionDate}
        onCancel={handleRemovalDeclined}
        onRemove={handleRemovalApproved}
      />
    </>
  );
};

export default CheckPointContent;
